// Localização: ./src/components/AIAssistant/MessageBubble.jsx
// Componente de mensagem do chat

import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';

/**
 * Componente de bolha de mensagem
 * @param {Object} props
 * @param {string} props.role - 'user', 'assistant', 'system'
 * @param {string} props.content - Conteúdo da mensagem
 * @param {Date} props.timestamp - Timestamp da mensagem
 * @param {string} props.type - Tipo de mensagem (para system: 'success', 'error', 'warning')
 */
const MessageBubble = ({ role, content, timestamp, type }) => {
    const { user } = useAuth();
    const isUser = role === 'user';
    const isSystem = role === 'system';
    
    // Obter foto do usuário ou usar placeholder
    const userPhotoUrl = user?.fotoURL ? getUsuarioFotoUrl(user.fotoURL) : '/images/higeia-avatar-shield.png';
    const aiPhotoUrl = '/images/perfil_ai.png';
    
    // Formata timestamp
    const formatTime = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    
    // Estilos baseados no tipo
    const getSystemStyles = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50 border-green-200 text-green-800';
            case 'error':
                return 'bg-red-50 border-red-200 text-red-800';
            case 'warning':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            default:
                return 'bg-gray-50 border-gray-200 text-gray-700';
        }
    };
    
    if (isSystem) {
        return (
            <div className="flex justify-center my-4">
                <div className={`px-4 py-2 rounded-lg border text-sm ${getSystemStyles()}`}>
                    {content}
                </div>
            </div>
        );
    }
    
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} my-3 transition-opacity duration-300 items-start gap-3`}>
            {/* Avatar - sempre no lado esquerdo */}
            <div className="flex-shrink-0">
                <img
                    src={isUser ? userPhotoUrl : aiPhotoUrl}
                    alt={isUser ? (user?.nome_completo || 'Usuário') : 'Assistente de IA'}
                    className={`w-12 h-12 rounded-full object-cover border-2 ${
                        isUser ? 'border-emerald-200' : 'border-gray-200'
                    }`}
                    style={{
                        objectFit: 'cover',
                        objectPosition: 'center',
                        minWidth: '48px',
                        minHeight: '48px'
                    }}
                    loading="eager"
                    decoding="async"
                    width="48"
                    height="48"
                    onError={(e) => {
                        // Fallback se a imagem não carregar
                        e.target.src = isUser ? '/images/higeia-avatar-shield.png' : '/images/perfil_ai.png';
                    }}
                />
            </div>
            
            {/* Mensagem */}
            <div className="max-w-[80%]">
                <div
                    className={`rounded-2xl px-4 py-3 transition-all duration-200 ${
                        isUser
                            ? 'bg-emerald-600 text-white shadow-md hover:shadow-lg'
                            : 'bg-white border border-gray-200 text-gray-800 shadow-sm hover:shadow-md'
                    }`}
                >
                    <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
                    {timestamp && (
                        <p
                            className={`text-xs mt-1 ${
                                isUser ? 'text-emerald-100' : 'text-gray-500'
                            }`}
                        >
                            {formatTime(timestamp)}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessageBubble;

